import React, { useCallback, useMemo, useRef, useState } from "react";
import { useUploadStore } from "@components/FileUpload/uploadStore";
import GenericDialog from "@components/GenericDialog/GenericDialog";
import StepperControls from "@components/StepperControls/StepperControls.tsx";
import StepperTabPanel from "@components/StepperTabs/StepperTabPanel.tsx";
import StepperTabs from "@components/StepperTabs/StepperTabs.tsx";
import {
  STEPPER_TYPE,
  StepStatus,
  type StepperTabsHandle,
  type StepStatusValue,
} from "@components/StepperTabs/StepperTabs.types.ts";
import { useStepStatuses } from "@components/StepperTabs/useStepStatuses";
import { useNotification } from "@context/NotificationContext.tsx";
import { NO_PASSPORT_KEY, steps } from "@features/port-security/components/utils";
import {
  getPortSecurityFormSchema,
  type PortSecurityFormData,
} from "@features/port-security/schema/portSecurityFormSchema";
import type { FieldValues } from "react-hook-form";
import { useFormState } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { LANG_BASE, type PortSecurityOutletContext } from "../SecurityClearanceContainer";
import { STEP_FIELDS } from "./constants";

type SCFormData = PortSecurityFormData & FieldValues;

const SecurityClearanceForm: React.FC = () => {
  const {
    formData,
    updateFormData,
    handleSubmit,
    cancel,
    submitPending,
    apiValidationErrorFields,
    visaExemptNationalities,
  } = useOutletContext<PortSecurityOutletContext>();
  const { errors: formErrors } = useFormState({ control: formData.control });
  const activeApiValidationErrorFields = useMemo(() => {
    const next = new Set<string>();
    for (const field of apiValidationErrorFields) {
      if (formErrors[field as keyof typeof formErrors]) {
        next.add(field);
      }
    }
    return next;
  }, [apiValidationErrorFields, formErrors]);
  const stepperTabsRef = useRef<StepperTabsHandle>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { notify } = useNotification();

  const [touchedSteps, setTouchedSteps] = useState<Record<number, boolean>>(
    Object.keys(STEP_FIELDS).reduce(
      (acc, key) => {
        acc[Number(key)] = false;
        return acc;
      },
      {} as Record<number, boolean>,
    ),
  );
  const [validateAllSteps, setValidateAllSteps] = useState(false);

  const validateAllStepsStatuses = useCallback(() => {
    setValidateAllSteps(true);
    setTouchedSteps(
      Object.keys(STEP_FIELDS).reduce(
        (acc, key) => {
          acc[Number(key)] = true;
          return acc;
        },
        {} as Record<number, boolean>,
      ),
    );
  }, []);

  const currentStepIndex = steps.findIndex((step) => location.pathname.endsWith(step.path));
  const isLastStep = currentStepIndex === steps.length - 1;
  const isFirstStep = currentStepIndex === 0;
  const goToNextStep = () => {
    const nextStep = steps[currentStepIndex + 1];
    if (nextStep) {
      const renewalQuery = new URLSearchParams(window.location.search);
      const nextPath = `${nextStep.path}${renewalQuery.size ? `?${renewalQuery.toString()}` : ""}`;
      navigate(nextPath);
      stepperTabsRef.current?.next();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const goToPreviousStep = () => {
    const prevStep = steps[currentStepIndex - 1];
    if (prevStep) {
      const renewalQuery = new URLSearchParams(window.location.search);
      const prevPath = `${prevStep.path}${renewalQuery.size ? `?${renewalQuery.toString()}` : ""}`;
      navigate(prevPath);
      stepperTabsRef.current?.back();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const { statusFor } = useStepStatuses<SCFormData>({
    schema: getPortSecurityFormSchema(t, { visaExemptNationalities }),
    form: formData,
    stepFields: STEP_FIELDS,
    currentStepIndex,
    touchedSteps,
    errors: formErrors,
    serverErrorFields: activeApiValidationErrorFields,
    validateAllSteps,
  });

  const getStatusForStep = (index: number): StepStatusValue =>
    // Match vessel-call: active step stays CURRENT; ERRORS show on other steps when
    // RHF or API field errors exist (including emailAddress).
    index === currentStepIndex ? StepStatus.CURRENT : statusFor(index);

  const isDocumentUploadPending = useUploadStore((state) =>
    Object.values(state.uploadsInFlightByKey).some((count) => count > 0),
  );

  // cancel button dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const dialogProps = {
    testId: "security-clearance-cancel-dialog",
    title: t(`${LANG_BASE}.cancel-dialog-title`),
    cancelButtonText: t(`${LANG_BASE}.cancel-dialog-cancel`),
    confirmButtonText: t(`${LANG_BASE}.cancel-dialog-confirm`),
    handleCloseDialog: () => setDialogOpen(false),
    confirmAction: () => {
      localStorage.removeItem(NO_PASSPORT_KEY);
      cancel();
      setDialogOpen(false);
    },
    elementContent: (
      <div className="text-left">
        <p>{t(`${LANG_BASE}.cancel-dialog-description`)}</p>
      </div>
    ),
  };

  return (
    <div className="pt-8 pb-8 px-24" id="stepper-container">
      <StepperTabs
        ref={stepperTabsRef}
        testId="form-stepper-tabs"
        onStepperTabChange={(index) => {
          setTouchedSteps((prev) => ({ ...prev, [currentStepIndex]: true }));
          const path = steps[index].path;
          const renewalQuery = new URLSearchParams(window.location.search);
          const targetPath = `${path}${renewalQuery.size ? `?${renewalQuery.toString()}` : ""}`;
          navigate(targetPath);
        }}
        currentStepIndex={currentStepIndex}
        stepperType={STEPPER_TYPE.VERTICAL}
        isTypeChangeable={false}
      >
        {steps.map((step, index) => (
          <StepperTabPanel
            key={step.path}
            label={t(`${step.label}`)}
            status={getStatusForStep(index)}
          >
            <div className="min-w-full flex flex-col bg-white">
              <Outlet context={{ formData, updateFormData, validateAllStepsStatuses }} />
              <div className="py-6 ">
                <StepperControls
                  prev={!isFirstStep ? goToPreviousStep : undefined}
                  next={isLastStep ? undefined : goToNextStep}
                  backLabelTranslationKey={steps[currentStepIndex - 1]?.label}
                  nextLabelTranslationKey={steps[currentStepIndex + 1]?.label}
                />
              </div>
            </div>
          </StepperTabPanel>
        ))}
      </StepperTabs>
      <div className="py-6 border-t border-t-[var(--stroke-secondary-stroke,#E6EDF2)]">
        <StepperControls
          cancel={() => setDialogOpen(true)}
          complete={() => {
            if (submitPending) {
              return;
            }
            if (isDocumentUploadPending) {
              notify({
                title: t("common.notification.please-wait-for-upload"),
                type: "warning",
              });
              return;
            }
            validateAllStepsStatuses();

            handleSubmit();
          }}
          completePending={submitPending}
          completeLabelTranslationKey="stepper-controls.submit"
        />
      </div>
      <GenericDialog open={dialogOpen} {...dialogProps} />
    </div>
  );
};

export default SecurityClearanceForm;
